import React, { useEffect, useState } from "react";
import Rating from "react-rating";
import axios from "../../../Axios";
import { Urls } from "../../../Urls";
import Loader from "../../Loader";
import styles from "./ViewLsp.module.css";
import NewProfileModal from "../../Shared/NewTransporterProfileModal/NewProfileModal";

/**
 * ViewLsp — displays shortlisted transporters for a spot enquiry.
 *
 * Fetches from: GET /api/spot/lorri_spot_v2?field=get_transporters&truck_enquiry_id=<id>
 * Falls back to item.benchmark_transporter_list / mastertransporterlist if no ID.
 */
const ViewLsp = ({ item = {}, planningbutton, mastertransporterlist }) => {
  const [lsp_transporter_list, setLsp_transporter_list] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTransporter, setActiveTransporter] = useState({ id: "", name: "" });
  const [openProfileModal, setOpenProfileModal] = useState(false);

  const handleOpenProfile = (transporter) => {
    setActiveTransporter({
      id: transporter.transporter_id,
      name: transporter.transporter_name,
    });
    setOpenProfileModal(true);
  };

  const handleCloseModal = () => {
    setOpenProfileModal(false);
    setActiveTransporter({ id: "", name: "" });
  };

  useEffect(() => {
    const truck_enquiry_id = item?.truck_enquiry_id;

    if (truck_enquiry_id) {
      setLoading(true);
      axios
        .get(`${Urls.spot}?field=get_transporters&truck_enquiry_id=${truck_enquiry_id}`)
        .then((res) => {
          const data = res?.data;
          const raw = Array.isArray(data)
            ? data
            : data?.benchmark_transporter_list ||
            data?.transporter_list ||
            data?.transporters ||
            data?.data ||
            [];
          const transporters = raw.map((t) => ({
            ...t,
            ischecked: t.isChecked ?? t.ischecked ?? false,
          }));
          setLsp_transporter_list(transporters);
        })
        .catch((err) => {
          console.error("Failed to fetch shortlisted transporters:", err);
          setLsp_transporter_list([]);
        })
        .finally(() => setLoading(false));
    } else {
      const fallback = planningbutton
        ? mastertransporterlist
        : item?.benchmark_transporter_list;
      setLsp_transporter_list(
        (fallback || []).map((t) => ({
          ...t,
          ischecked: t.isChecked ?? t.ischecked ?? false,
        }))
      );
    }
  }, [item?.truck_enquiry_id]); // eslint-disable-line

  const checkedCount = lsp_transporter_list.filter((t) => t.ischecked).length;

  return (
    <>
      <Loader show={loading} />

      <div className={styles.container}>
        {/* Summary bar */}
        {!loading && lsp_transporter_list.length > 0 && (
          <div className={styles.summaryBar}>
            <span className={styles.summaryCount}>
              <i className="fa fa-users" style={{ marginRight: "8px" }} />
              {lsp_transporter_list.length}{" "}
              {lsp_transporter_list.length === 1 ? "Transporter" : "Transporters"} Shortlisted
            </span>
          </div>
        )}

        {/* Transporter list */}
        <div className={styles.transporterListContainer}>
          {loading ? (
            [1, 2, 3].map((n) => <div key={n} className={styles.skeletonCard} />)
          ) : lsp_transporter_list.length > 0 ? (
            lsp_transporter_list.map((transporter, index) => (
              <div className={styles.transporterCard} key={`t-${index}`}>
                {/* Serial number */}
                <span className={styles.serialNum}>{index + 1}</span>

                {/* Name */}
                <div className={styles.nameWrap}>
                  <button
                    className={styles.profileButton}
                    onClick={() => handleOpenProfile(transporter)}
                    title={transporter.transporter_name}
                  >
                    {transporter.transporter_name}
                  </button>
                </div>

                {/* Rating */}
                <div className={styles.ratingWrap}>
                  <Rating
                    initialRating={transporter.overall_rating || 0}
                    readonly
                    emptySymbol={`fa fa-star-o ${styles.starEmpty}`}
                    fullSymbol={`fa fa-star ${styles.starFull}`}
                  />
                </div>
              </div>
            ))
          ) : (
            <div className={styles.noTransporters}>
              <i className={`fa fa-inbox ${styles.emptyIcon}`} />
              <span>No transporters have been shortlisted for this enquiry.</span>
            </div>
          )}
        </div>
      </div>

      {openProfileModal && activeTransporter?.id !== "" && (
        <NewProfileModal
          isOpen={openProfileModal}
          onClose={handleCloseModal}
          transporter_id={activeTransporter.id}
          transporter_name={activeTransporter.name}
          screenName="SPOT - LSP shortlist"
          moduleName="SPOT - LSP shortlist"
          transporter_list={lsp_transporter_list}
        />
      )}
    </>
  );
};

export default ViewLsp;
